import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Input, Select, BackButton, ContentLoader } from '@/components/common';
import { AddressFields } from '@/components/forms/AddressFields';
import { getErrorMessage, getFieldErrors, NON_FIELD_KEY } from '@/services/api';
import { digitsOnlyOnChange, nameOnChange } from '@/utils/validation';
import { ShareCustomerLink } from './ShareCustomerLink';
import {
  needsProposal,
  useInsurerProposal,
  useIssuanceReadiness,
  useQuoteOptions,
  useSaveProposal,
  useStoredProposal,
  type ProposalDetailsPayload,
} from './proposalFlow';

/**
 * BOARD #245 part 2 -- the PROPOSAL step of Quote -> Proposal -> KYC ->
 * Payment -> Document.
 *
 * WHAT WAS WRONG HERE. This form collected fourteen answers and POSTed them,
 * via `useCreateProposal`, to `/integrations/policies/issue/` under the keys
 * `{quote_request_id, selected_insurer, proposer, nominee, kyc,
 * addons_selected}`. `PolicyIssueSerializer` declares none of them and requires
 * six others, so every submission was a 400; the `onSuccess` branch then read
 * `response.policy_number`, a key that endpoint does not return on that path,
 * and navigated to `/quotes/payment/undefined`. Nothing was ever written and
 * nothing was ever sent to any insurer -- and even had the request validated,
 * `PolicyIssuanceService` builds its proposal with
 * `ProposerInfo(name="", email="", phone="")`.
 *
 * WHAT IT DOES NOW. It POSTs to `POST /api/v1/quotes/requests/{id}/proposal/`,
 * which persists the proposer onto `QuoteRequest.proposer_*` and the address
 * into `additional_data["address"]` -- the exact places
 * `PolicyService.create_policy_from_quote` reads when a quote becomes a Policy.
 * That means the answers survive whether the policy is issued through an
 * insurer API or by the broker uploading the issued copy, which matters on a
 * platform where the relay is dormant and no insurer-priced quote has ever
 * been persisted.
 *
 * KYC IS NOT COLLECTED HERE ANY MORE. It has its own ladder -- the customer
 * submits it on their public link, the broker approves or rejects it, and
 * `record-payment` is gated on that approval. The `KYCUpload` widget on this
 * screen posted to nothing at all: `kyc` rode inside the payload that 400'd.
 * Offering a second, non-functioning door into KYC is worse than pointing at
 * the working one, so the card now links to the quote instead.
 *
 * The route is `/quotes/proposal/:requestId/:insurerCode` (App.tsx:247); the
 * option is resolved from those two rather than passed in navigation state, so
 * the page survives a refresh and a bookmarked link.
 */

/**
 * The client-side rules are kept DELIBERATELY IN STEP with
 * `ProposalDetailsSerializer`, which is the one that actually decides. Where
 * the server accepts a blank (`last_name`, `email`, `gender`, `pan_number`, and
 * the whole nominee block) this does too -- a field the browser demands and the
 * API does not is a rule with no owner, and it is how a broker gets stuck on a
 * screen over an answer nobody needs.
 *
 * PAN and pincode formats match `core.security.validate_pan` /
 * `validate_pincode` exactly, so the client never rejects what the server would
 * take, or takes what it would reject.
 */
const proposalSchema = z.object({
  // Proposer Details
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().optional().default(''),
  email: z.string().email('Invalid email address').or(z.literal('')).optional().default(''),
  mobile: z.string().min(10, 'Mobile number must be at least 10 digits').regex(/^\d+$/, 'Must contain only digits'),
  date_of_birth: z.string().optional().default(''),
  gender: z.enum(['male', 'female', 'other']).or(z.literal('')).optional().default(''),
  pan_number: z
    .string()
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN format (e.g., ABCDE1234F)')
    .or(z.literal(''))
    .optional()
    .default(''),

  // Address
  flat_number: z.string().optional().default(''),
  street: z.string().optional().default(''),
  city: z.string().optional().default(''),
  state: z.string().optional().default(''),
  pincode: z.string().regex(/^\d{6}$/, 'Pincode must be 6 digits').or(z.literal('')).optional().default(''),

  // Nominee
  nominee_first_name: z.string().optional().default(''),
  nominee_last_name: z.string().optional().default(''),
  nominee_date_of_birth: z.string().optional().default(''),
  nominee_relation: z.string().optional().default(''),
});

type ProposalFormData = z.infer<typeof proposalSchema>;

const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const relationOptions = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'child', label: 'Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'other', label: 'Other' },
];

export function ProposalFormPage() {
  const { requestId, insurerCode } = useParams<{ requestId: string; insurerCode: string }>();
  // `?quote=<id>` names the exact option. One insurer can price the same
  // request up to three ways (comprehensive / TP-only / own-damage only), so
  // the insurer code alone would silently pick whichever row came first -- and
  // the proposal would go to the insurer for a cover nobody chose.
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get('quote');
  const navigate = useNavigate();
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  // The option this proposal is FOR. Resolved from the two route params rather
  // than carried in navigation state, so a refresh or a pasted URL still works.
  const { data: options, isLoading: optionsLoading } = useQuoteOptions(requestId);
  // A named option that is not there is NOT replaced by another row of the
  // same insurer: that row may be a different cover at a different premium.
  const option = useMemo(
    () =>
      quoteId
        ? (options ?? []).find((o) => o.id === quoteId && o.insurer_code === insurerCode)
        : (options ?? []).find((o) => o.insurer_code === insurerCode),
    [options, insurerCode, quoteId]
  );

  // What the server already holds, so coming back to finish the form does not
  // mean retyping it.
  const { data: stored, isLoading: storedLoading } = useStoredProposal(requestId);

  // Answered from configuration alone -- no insurer is called. Its `blockers`
  // are rendered verbatim below.
  const { data: readiness } = useIssuanceReadiness(option?.id);
  // BOARD #258. The insurer's own proposal -- a separate act from recording the
  // answers on our row above, and one that could not be performed at all before
  // this batch: `create_proposal` was written and unreachable, so GoDigit had
  // never been sent a proposal by this platform. It is opened AFTER the details
  // are saved, because the payload is built from what we just wrote down.
  const insurerProposal = useInsurerProposal(option?.id);

  const saveMutation = useSaveProposal(requestId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProposalFormData>({
    resolver: zodResolver(proposalSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    const saved = stored?.proposal;
    if (!saved || Object.keys(saved).length === 0) return;
    const address = saved.address ?? {};
    const nominee = saved.nominee ?? {};
    reset({
      first_name: saved.first_name ?? '',
      last_name: saved.last_name ?? '',
      email: saved.email ?? '',
      mobile: saved.mobile ?? '',
      date_of_birth: saved.date_of_birth ?? '',
      gender: (saved.gender ?? '') as ProposalFormData['gender'],
      pan_number: saved.pan_number ?? '',
      flat_number: address.flat_number ?? '',
      street: address.street ?? '',
      city: address.city ?? '',
      state: address.state ?? '',
      pincode: address.pincode ?? '',
      nominee_first_name: nominee.first_name ?? '',
      nominee_last_name: nominee.last_name ?? '',
      nominee_date_of_birth: nominee.date_of_birth ?? '',
      nominee_relation: nominee.relation ?? '',
    });
  }, [stored, reset]);

  const onSubmit = (data: ProposalFormData) => {
    setServerErrors({});
    // One flat object, exactly the fields `ProposalDetailsSerializer` declares.
    const payload: ProposalDetailsPayload = {
      first_name: data.first_name,
      last_name: data.last_name ?? '',
      email: data.email ?? '',
      mobile: data.mobile,
      date_of_birth: data.date_of_birth ?? '',
      gender: data.gender ?? '',
      pan_number: data.pan_number ?? '',
      flat_number: data.flat_number ?? '',
      street: data.street ?? '',
      city: data.city ?? '',
      state: data.state ?? '',
      pincode: data.pincode ?? '',
      nominee_first_name: data.nominee_first_name ?? '',
      nominee_last_name: data.nominee_last_name ?? '',
      nominee_date_of_birth: data.nominee_date_of_birth ?? '',
      nominee_relation: data.nominee_relation ?? '',
    };

    saveMutation.mutate(payload, {
      onSuccess: async () => {
        toast.success('Proposal details saved');

        // BOARD #258. Open the insurer's proposal, but ONLY when the server
        // says this adapter has a proposal step and does not already hold one
        // (`needs_proposal`). Never on our own guess: a call the broker did not
        // ask for, made because a cached readiness was missing a field, is how
        // a second application gets opened on the insurer's book.
        //
        // A failure here does NOT take the saved details away. They are ours,
        // they are already written, and the rest of the flow -- record the
        // payment, upload the issued copy -- works without the insurer's API.
        if (option && needsProposal(readiness)) {
          try {
            const result = await insurerProposal.mutateAsync({ mode: 'create' });
            toast.success(
              `Proposal ${result.proposal.application_id} opened with ${result.insurer_code}`
            );
          } catch (error) {
            toast.error(
              `Your details are saved, but the insurer proposal could not be opened: ${getErrorMessage(error)}`
            );
          }
        }

        // Keyed by the OPTION, not by a policy number: no policy exists yet.
        // `PolicyIssueView` requires a payment before it will issue one, so the
        // old navigate to `/quotes/payment/${response.policy_number}` was
        // pointing at a value the server had not produced and could not have.
        if (option) {
          navigate(`/quotes/payment/${option.id}`);
        } else {
          navigate(`/quotes/compare/${requestId}`);
        }
      },
      onError: (error) => {
        setServerErrors(getFieldErrors(error));
        toast.error(getErrorMessage(error));
      },
    });
  };

  const nonFieldError = serverErrors[NON_FIELD_KEY];

  /** The client rule if it fired, otherwise whatever the SERVER said about that
   *  field. The server is the authority -- `ProposalDetailsSerializer` runs
   *  `core.security.validate_pan` and the rest -- so its message must be able
   *  to reach the input, not just the toast. */
  const fieldError = (name: keyof ProposalFormData) =>
    errors[name]?.message ?? serverErrors[name];

  if (optionsLoading || storedLoading) {
    return <ContentLoader />;
  }

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/quotes" />
      </div>
      <PageHeader
        title="Create Proposal"
        subtitle={`Insurer: ${insurerCode}`}
        breadcrumbs={[
          { label: 'Quotes', href: '/quotes' },
          { label: 'Compare', href: `/quotes/compare/${requestId}` },
          { label: 'Proposal' },
        ]}
        actions={
          <Link to={`/quotes/compare/${requestId}`}>
            <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
              Back to Quotes
            </Button>
          </Link>
        }
      />

      {requestId && (
        <div className="mb-4">
          <ShareCustomerLink requestId={requestId} />
        </div>
      )}

      {/* The option this proposal is for. Shows the real premium off the stored
          quote rather than the insurer code alone -- the broker is about to
          commit a customer to a number, so the number belongs on the screen. */}
      <Card className="mb-6 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Selected Insurer</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {option?.insurer_name ?? insurerCode}
              {option?.cover_label ? ` \u2014 ${option.cover_label}` : ''}
            </p>
            {option && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {option.pricing_source === 'insurer_api'
                  ? `Priced by the insurer's API (ref ${option.insurer_quote_id})`
                  : 'Premium entered by hand'}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 dark:text-gray-400">Premium</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {option ? option.total_premium : '--'}
            </p>
          </div>
        </div>
      </Card>

      {/* The option named in the URL is not on this request. Say so; do not
          render a form whose submission has nowhere to go. */}
      {!option && (
        <Card className="mb-6 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                {quoteId ? 'This option no longer exists' : `No option from ${insurerCode} on this quote`}
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                The proposal can still be recorded against the quote request, but there is no priced
                option to take to payment. Go back and pick one.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* HONEST BLOCKED STATES, ahead of the work rather than three screens
          into it. Every sentence is the server's own -- several are word for
          word what `PaymentInitiateView` or `PolicyIssueView` would answer. */}
      {readiness && !readiness.can_issue_via_api && readiness.blockers.length > 0 && (
        <Card className="mb-6 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                This option cannot be issued through the insurer&apos;s API
              </p>
              <ul className="mt-2 space-y-1">
                {readiness.blockers.map((blocker) => (
                  <li key={blocker} className="text-xs text-yellow-700 dark:text-yellow-300">
                    {blocker}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                Recording the proposal below still works and still reaches the issued policy. The
                policy itself will have to be issued the usual way and its copy uploaded on the
                quote.
              </p>
            </div>
          </div>
        </Card>
      )}

      {nonFieldError && (
        <Card className="mb-6 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <p className="text-sm text-red-700 dark:text-red-300">{nonFieldError}</p>
        </Card>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Proposer Details */}
          <Card>
            <CardHeader title="Proposer Details" subtitle="Personal information of the policy holder" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                label="First Name"
                error={fieldError('first_name')}
                required
                {...register('first_name', { onChange: nameOnChange })}
              />
              <Input
                label="Last Name"
                error={fieldError('last_name')}
                {...register('last_name', { onChange: nameOnChange })}
              />
              <Input
                label="Email"
                type="email"
                placeholder="name@example.com"
                error={fieldError('email')}
                {...register('email')}
              />
              <Input
                label="Mobile Number"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="9999999999"
                error={fieldError('mobile')}
                required
                {...register('mobile', { onChange: digitsOnlyOnChange })}
              />
              <Input
                label="Date of Birth"
                type="date"
                error={fieldError('date_of_birth')}
                {...register('date_of_birth')}
              />
              <Select
                label="Gender"
                options={genderOptions}
                placeholder="Select gender..."
                error={fieldError('gender')}
                {...register('gender')}
              />
              <Input
                label="PAN Number"
                placeholder="ABCDE1234F"
                error={fieldError('pan_number')}
                {...register('pan_number')}
              />
            </div>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader title="Address" subtitle="Residential address" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Input
                label="Flat/House Number"
                error={fieldError('flat_number')}
                {...register('flat_number')}
              />
              <Input
                label="Street"
                error={fieldError('street')}
                {...register('street')}
              />
              {/* B6: PIN pre-fills State (and City); State stays a dropdown
                  of the real GET /geography/states/lookup/ list (36
                  states/UTs) instead of the hardcoded `stateOptions` this
                  used to render from. Stored as `state.code`
                  (`stateValueKey="code"`) -- whatever code the States master
                  carries, not necessarily the old two-letter code, since 0
                  saved proposals had an address on file to preserve a shape
                  for. */}
              <AddressFields
                pincode={watch('pincode') ?? ''}
                onPincodeChange={(value) => setValue('pincode', value, { shouldValidate: true, shouldDirty: true })}
                pincodeLabel="Pincode"
                pincodeError={fieldError('pincode')}
                city={watch('city') ?? ''}
                onCityChange={(value) => setValue('city', value, { shouldValidate: true, shouldDirty: true })}
                cityError={fieldError('city')}
                state={watch('state') ?? ''}
                onStateChange={(value) => setValue('state', value, { shouldValidate: true, shouldDirty: true })}
                stateError={fieldError('state')}
                stateValueKey="code"
              />
            </div>
          </Card>

          {/* Nominee */}
          <Card>
            <CardHeader title="Nominee Details" subtitle="Nominee information for the policy" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Input
                label="Nominee First Name"
                error={fieldError('nominee_first_name')}
                {...register('nominee_first_name')}
              />
              <Input
                label="Nominee Last Name"
                error={fieldError('nominee_last_name')}
                {...register('nominee_last_name')}
              />
              <Input
                label="Nominee Date of Birth"
                type="date"
                error={fieldError('nominee_date_of_birth')}
                {...register('nominee_date_of_birth')}
              />
              <Select
                label="Relation"
                options={relationOptions}
                placeholder="Select relation..."
                error={fieldError('nominee_relation')}
                {...register('nominee_relation')}
              />
            </div>
          </Card>

          {/* KYC. NOT collected here.
              The widget that used to sit on this card wrote to nothing: its
              value rode inside the payload that 400'd, so every document a
              broker attached here was discarded. The working ladder is the
              customer's public link -> broker approve/reject -> record-payment,
              and `record_payment` refuses anything that is not `kyc_approved`,
              so pointing at it is not a downgrade -- it is the only door that
              opens the next gate. */}
          <Card>
            <CardHeader
              title="KYC Documents"
              subtitle="Collected from the customer on their quote link, then approved here"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              KYC is submitted by the customer on the quote link you shared with them and reviewed on
              the quote itself. Payment cannot be recorded until it has been approved.
            </p>
            <Link to={`/quotes/${requestId}`} className="inline-block mt-3">
              <Button variant="outline" size="sm" type="button">
                Open the quote to review KYC
              </Button>
            </Link>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Link to={`/quotes/compare/${requestId}`}>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              isLoading={saveMutation.isPending}
            >
              Save Proposal &amp; Continue
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
